import {
  createBundleGroupId,
  BUNDLE_GROUP_PROP,
  BUNDLE_ROLE_PROP,
  BUNDLE_QTY_LOCK_PROP,
} from '@theme/bundle-cart-guard';

/**
 * Shared buy-now / checkout helper for sticky bar & anchor nav CTAs.
 * Uses the same variant-level bundle rules as product-form buy buttons.
 */
function parseBundleConfig(el) {
  if (!el?.textContent) return { bundleRules: {}, qtyLock: true };
  try {
    const parsed = JSON.parse(el.textContent);
    return {
      bundleRules: parsed?.bundleRules && typeof parsed.bundleRules === 'object' ? parsed.bundleRules : {},
      qtyLock: parsed?.qtyLock !== false,
    };
  } catch {
    return { bundleRules: {}, qtyLock: true };
  }
}

function readBundleConfig(scope) {
  const fromForm = document.querySelector('product-form-component [data-bundle-config]');
  if (fromForm) return parseBundleConfig(fromForm);

  const scoped = scope?.querySelector?.('[data-bundle-config]');
  if (scoped) return parseBundleConfig(scoped);

  return parseBundleConfig(document.querySelector('[data-bundle-config]'));
}

function resolveCompanionVariantId(mainVariantId, config) {
  if (!mainVariantId) return '';
  return String(config?.bundleRules?.[String(mainVariantId)] || '').trim();
}

function buildBundleItems(mainVariantId, companionVariantId, quantity, qtyLock) {
  const groupId = createBundleGroupId();
  const qty = Math.max(1, quantity || 1);

  /** @type {Array<{ id: number, quantity: number, properties: Record<string, string> }>} */
  const items = [
    {
      id: Number(mainVariantId),
      quantity: qty,
      properties: {
        [BUNDLE_GROUP_PROP]: groupId,
        [BUNDLE_ROLE_PROP]: 'parent',
      },
    },
  ];

  if (companionVariantId) {
    /** @type {Record<string, string>} */
    const childProps = {
      [BUNDLE_GROUP_PROP]: groupId,
      [BUNDLE_ROLE_PROP]: 'child',
    };
    if (qtyLock !== false) childProps[BUNDLE_QTY_LOCK_PROP] = '1';

    items.push({
      id: Number(companionVariantId),
      quantity: qty,
      properties: childProps,
    });
  }

  return items;
}

async function addItems(items) {
  const cartAddUrl = window.Theme?.routes?.cart_add_url || '/cart/add.js';
  const response = await fetch(cartAddUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({ items }),
  });
  return response.json();
}

/**
 * @param {object} options
 * @param {string|number} options.mainVariantId
 * @param {number} [options.quantity]
 * @param {ParentNode|null} [options.scope]
 * @returns {Promise<boolean>} true if navigation started / completed flow
 */
async function buyNow({ mainVariantId, quantity = 1, scope = null } = {}) {
  const id = String(mainVariantId || '').trim();
  if (!id) return false;

  const config = readBundleConfig(scope);
  const companionVariantId = resolveCompanionVariantId(id, config);

  if (!companionVariantId) {
    window.location.assign(`/cart/${id}:1?checkout`);
    return true;
  }

  await fetch('/cart/clear.js', {
    method: 'POST',
    headers: { Accept: 'application/json' },
  });

  const items = buildBundleItems(id, companionVariantId, quantity, config.qtyLock);
  const result = await addItems(items);

  if (result?.status) {
    window.alert(result.message || result.description || 'Checkout failed');
    return false;
  }

  window.location.assign('/checkout');
  return true;
}

window.ThemeBundleCheckout = {
  readBundleConfig,
  resolveCompanionVariantId,
  buyNow,
};
